import {
  Button,
  Icon,
  MASKED_VALUE,
  useCopyToClipboard,
} from '@dittolive/anvil'
import {
  CheckIcon,
  CopyIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@phosphor-icons/react'
import React, { useMemo, useState } from 'react'

import type {
  LiveQuerySinkWebhookConfig,
  LiveQueryWebhookDestination,
} from '../../../types/dataBridge'
import DataBridgeSectionRowContainer from '../components/DataBridgeSectionRowContainer'
import { defaultStringActionRenderer } from '../components/DataBridgeStringFieldCopyAction'

type Props = {
  destination: LiveQueryWebhookDestination
  config: LiveQuerySinkWebhookConfig | null
}

export default function WebhookDestination({
  destination: {
    webhook: { url },
  },
  config,
}: Props) {
  const [isMasked, setIsMasked] = useState(true)
  const apiSecret = config?.webhook
  const [doCopy, recentlyCopied, canCopy] = useCopyToClipboard(
    apiSecret || '',
    2000,
  )

  const secret = useMemo(
    () => (apiSecret ? (isMasked ? MASKED_VALUE : apiSecret) : undefined),
    [isMasked, apiSecret],
  )

  const renderSecret = () => {
    if (!secret) {
      return (
        <div
          title="Secret pending creation"
          data-testid="secretPlaceholder"
          className="bg-background-surface-secondary h-5 w-3/5 animate-pulse rounded"
        />
      )
    } else {
      return (
        <p className="text-foreground-subtle overflow-hidden text-ellipsis whitespace-nowrap text-base leading-5">
          {secret}
        </p>
      )
    }
  }

  return (
    <>
      <DataBridgeSectionRowContainer data-testid="urlSegment">
        <div className="flex flex-1 flex-col space-y-1">
          <p className="text-foreground-normal text-base font-medium leading-5">
            URL
          </p>
          <p className="text-foreground-subtle text-base leading-5">{url}</p>
        </div>

        {defaultStringActionRenderer(url)}
      </DataBridgeSectionRowContainer>

      <DataBridgeSectionRowContainer data-testid="secretSegment">
        <div className="flex min-w-0 flex-1 flex-col space-y-1">
          <p className="text-foreground-normal text-base font-medium leading-5">
            Secret
          </p>
          {renderSecret()}
        </div>

        <div className="flex gap-x-2">
          <Button
            variant="ghost"
            size="icon"
            className="focus-visible:outline-focus-outline flex shrink-0 outline-none focus-visible:outline-2 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="visibilityIcon"
            onClick={() => setIsMasked(!isMasked)}
            disabled={!apiSecret}
          >
            <Icon
              svg={isMasked ? <EyeIcon /> : <EyeSlashIcon />}
              className="text-foreground-subtle"
            />
          </Button>

          {canCopy && (
            <Button
              variant="ghost"
              size="icon"
              onClick={doCopy}
              className="focus-visible:outline-focus-outline flex shrink-0 outline-none focus-visible:outline-2 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="copySecretButton"
              disabled={!apiSecret}
            >
              {recentlyCopied ? (
                <Icon
                  svg={<CheckIcon />}
                  className="text-green-500"
                  data-testid="recentlyCopiedIcon"
                />
              ) : (
                <Icon
                  svg={<CopyIcon />}
                  className="text-foreground-subtle"
                  data-testid="copyIcon"
                />
              )}
            </Button>
          )}
        </div>
      </DataBridgeSectionRowContainer>
    </>
  )
}
